import React, { useMemo, useState } from "react";
import { PRODUCTS } from "@/data/products";
import ProductCatalogCard, { ProductCatalogGrid } from "@/components/site/ProductCatalogCard";
import ProductSearch from "@/components/site/ProductSearch";

const summarizeSpecs = (product) => {
  const s = product.specs || {};
  return [
    s["Material"],
    s["Personalização"],
    `Pedido mínimo: ${s["Pedido mínimo"] || "—"}`,
    `Prazo: ${s["Prazo"] || "—"}`,
  ].filter(Boolean);
};

/** Accent-insensitive lowercase normalize (matches Catalog's behaviour). */
const normalize = (str = "") =>
  str
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

const buildSearchable = (p) =>
  normalize(
    [
      p.name,
      p.short,
      p.slug,
      p.tagline,
      p.description,
      p.category,
      Array.isArray(p.keywords) ? p.keywords.join(" ") : p.keywords,
      Array.isArray(p.benefits) ? p.benefits.join(" ") : "",
      p.specs ? Object.values(p.specs).join(" ") : "",
    ]
      .filter(Boolean)
      .join(" ")
  );

export const ProductsSection = ({ onQuoteClick }) => {
  const [query, setQuery] = useState("");

  // Base list: exclude legacy toalha slug, keep prior behaviour.
  const baseItems = useMemo(
    () =>
      PRODUCTS.filter((p) => p.slug !== "toalha-personalizada").map((p) => ({
        slug: p.slug,
        name: p.name,
        code: p.short,
        image: p.image,
        features: summarizeSpecs(p),
        link: `/produtos/${p.slug}`,
        badge: p.badge,
        search: buildSearchable(p),
      })),
    []
  );

  const items = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return baseItems;
    const tokens = q.split(/\s+/);
    return baseItems.filter((p) => tokens.every((t) => p.search.includes(t)));
  }, [baseItems, query]);

  return (
    <section id="produtos" data-testid="products-section" className="mn-section bg-white">
      <div className="mn-container">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
          <div className="lg:col-span-7">
            <span className="mn-eyebrow">Catálogo</span>
            <h2 className="mn-h-display text-[2.25rem] sm:text-[2.75rem] lg:text-[3.25rem] mt-4">
              Linha completa de brindes <br className="hidden md:block" />
              <span style={{ color: "var(--mn-gold)" }}>personalizados para empresas.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[var(--mn-dark)] leading-relaxed">
              Produtos selecionados pensando no seu evento, ação interna ou campanha. Materiais de qualidade, personalização precisa e ótimo acabamento — para que sua marca seja sempre lembrada.
            </p>
          </div>
        </div>

        {/* Search bar — sits directly above the product grid on Home */}
        <div className="mb-10 lg:mb-12" data-testid="home-search-wrap">
          <ProductSearch
            testId="home-product-search"
            value={query}
            onChange={setQuery}
            onClear={() => setQuery("")}
            resultCount={query ? items.length : undefined}
          />
        </div>

        {items.length > 0 ? (
          <ProductCatalogGrid testId="products-grid">
            {items.map((p) => (
              <ProductCatalogCard
                key={p.slug}
                name={p.name}
                code={p.code}
                image={p.image}
                features={p.features}
                link={p.link}
                badge={p.badge}
                onQuote={() => onQuoteClick?.(p.name)}
                testIdSlug={p.slug}
              />
            ))}
          </ProductCatalogGrid>
        ) : (
          <div
            className="border border-dashed border-[var(--mn-line)] rounded-2xl py-16 px-6 text-center bg-[#FAFAFA]"
            data-testid="products-empty"
          >
            <p className="font-serif-display font-bold text-xl sm:text-2xl text-[var(--mn-black)]">
              Nenhum produto encontrado.
            </p>
            <p className="mt-3 text-sm text-[var(--mn-dark)]">
              Tente outro termo ou{" "}
              <button
                type="button"
                onClick={() => setQuery("")}
                className="underline underline-offset-4 text-[var(--mn-gold)] hover:text-[var(--mn-gold-hover)]"
                data-testid="products-empty-reset"
              >
                limpe a pesquisa
              </button>
              .
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductsSection;
